import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { ListOrdered, GitBranch, Flag, Star, RotateCcw, Maximize2, Minimize2, PanelsTopLeft, X } from 'lucide-react';
import type { RaceRecording } from '../../shared/race';
import TimingTower from './TimingTower';
import DriverLapHistory from './DriverLapHistory';
import type { SessionResult } from '../../shared/overview';
import type { SessionView } from '../../shared/session-view';
import RaceStudio, { type StudioPlayback } from './RaceStudio';
import RaceRecords from './RaceRecords';
import { WeatherStrip } from './WeatherStrip';
import RaceFeed from './RaceFeed';
import { useSessionChart } from '../hooks/useSessionChart';
import PanelResizer from './PanelResizer';
import { useElementSize } from './useElementSize';
import { DEFAULT_PANEL_WIDTHS, FAVORITES_STORAGE_KEY, LAYOUT_STORAGE_KEY, fitPanelWidths, isFavorite, parseFavorites, parsePanelWidths, toggleFavorite } from './dashboardPreferences';
import '../styles/dashboard.css';

export interface DashboardProps {
  selectedDriver: number;
  onSelectDriver: (number: number) => void;
  stale?: boolean;
  recording: RaceRecording;
  /** Unified read model for all panels at the current cursor. */
  sessionView: SessionView;
  result?: SessionResult | null;
  presentation?: 'live' | 'results' | 'replay';
  reviewAtMs?: number | null;
  onReviewAt?: (atMs: number) => void;
  playback: StudioPlayback;
  followingLive?: boolean;
  liveHeadAtMs?: number;
  onReturnLive?: () => void;
  animateUpdates?: boolean;
  onInspect?: () => void;
  processNotice?: string | null;
}

const mobileViews = [
  { id: 'timing', label: '计时', icon: ListOrdered },
  { id: 'field', label: '比赛走势', icon: GitBranch },
  { id: 'messages', label: '消息', icon: Flag },
] as const;
type MobileView = typeof mobileViews[number]['id'];

function readLocal(key: string) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function saveLocal(key: string, value: unknown) {
  try { window.localStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; }
}
const VIEWS_KEY = 'f1live.dashboard.lap-history.v1';

export default function Dashboard({ selectedDriver, onSelectDriver, recording, sessionView, result, presentation = 'live', reviewAtMs, onReviewAt, playback, followingLive = false, liveHeadAtMs, onReturnLive, animateUpdates = false, onInspect, processNotice }: DashboardProps) {
  const viewSnapshot = sessionView.snapshot;
  const results = presentation === 'results' && Boolean(result);
  // Extremes change at observation boundaries, not when a browser paints a frame.
  const records = sessionView.records;
  const chart = useSessionChart({ recording, sessionView, presentation, followingLive, liveHeadAtMs });
  const [mobileView, setMobileView] = useState<MobileView>('timing');
  const [savedWidths, setSavedWidths] = useState(() => parsePanelWidths(readLocal(LAYOUT_STORAGE_KEY)));
  const [showLapHistory, setShowLapHistory] = useState(() => readLocal(VIEWS_KEY) === 'true');
  const layoutMenu = useRef<HTMLDetailsElement>(null);
  const [favorites, setFavorites] = useState(() => parseFavorites(readLocal(FAVORITES_STORAGE_KEY)));
  const [compareNumber, setCompareNumber] = useState<number | null>(null);
  const [fullscreen, setFullscreen] = useState(Boolean(document.fullscreenElement));
  const [notice, setNotice] = useState('');
  const pitwallRef = useRef<HTMLDivElement>(null);
  const grid = useElementSize(1366, 650);
  const widths = fitPanelWidths(savedWidths, grid.width);
  const driver = viewSnapshot.drivers.find(item => item.number === selectedDriver) ?? viewSnapshot.drivers.find(item => item.position === 1) ?? viewSnapshot.drivers[0];
  const compareDriver = viewSnapshot.drivers.find(item => item.number === compareNumber && item.number !== driver?.number);
  const favoriteDrivers = useMemo(() => favorites.flatMap(favorite => { const current = viewSnapshot.drivers.find(item => isFavorite(favorite, item)); return current ? [current] : []; }), [favorites, viewSnapshot.drivers]);
  const selectedIsFavorite = driver ? favorites.some(favorite => isFavorite(favorite, driver)) : false;

  useEffect(() => {
    if (!saveLocal(LAYOUT_STORAGE_KEY, { version: 1, widths: savedWidths })) setNotice('浏览器未允许保存布局；当前调整仍然有效。');
  }, [savedWidths]);
  useEffect(() => {
    if (!saveLocal(FAVORITES_STORAGE_KEY, { version: 1, drivers: favorites })) setNotice('浏览器未允许保存关注列表；当前会话内仍然有效。');
  }, [favorites]);
  useEffect(() => { saveLocal(VIEWS_KEY, showLapHistory); }, [showLapHistory]);
  useEffect(() => {
    const close = (event: PointerEvent) => { if (layoutMenu.current && !layoutMenu.current.contains(event.target as Node)) layoutMenu.current.open = false; };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);
  useEffect(() => { setCompareNumber(null); }, [viewSnapshot.session.key]);
  useEffect(() => {
    if (compareNumber === driver?.number) setCompareNumber(null);
  }, [driver?.number, compareNumber]);
  useEffect(() => {
    const update = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, []);

  const selectDriver = useCallback((number: number) => {
    onInspect?.();
    if (number === compareNumber) setCompareNumber(driver?.number ?? null);
    onSelectDriver(number);
    // Keep the user's mobile pane and selected driver identity stable.
  }, [onInspect, compareNumber, driver?.number, onSelectDriver]);
  function inspectLap(lap: number) {
    if (!driver) return;
    const completion = sessionView.facts.flatMap(event => event.type === 'timing' && event.driverNumber === driver.number ? event.patch.recentLaps ?? [] : [])
      .find(item => item.lap === lap && !item.deleted);
    if (completion) onReviewAt?.(completion.atMs);
  }
  async function toggleFullscreen() {
    if (!document.fullscreenEnabled) { setNotice('当前浏览器不支持页面全屏，可使用浏览器自身的全屏功能。'); return; }
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else {
        const target = pitwallRef.current?.closest<HTMLElement>('.app-shell') ?? pitwallRef.current;
        if (target) await target.requestFullscreen();
      }
      setNotice('');
    } catch { setNotice('无法切换全屏：浏览器可能限制了此操作。可使用浏览器自身的全屏功能。'); }
  }

  return <div ref={pitwallRef} className={`pitwall free-pitwall mobile-view-${mobileView} presentation-${presentation}`}>
    <RaceRecords sessionView={sessionView} onSelectDriver={selectDriver} onReviewAt={onReviewAt} animateUpdates={animateUpdates}/>
    <div className="dashboard-toolbar" aria-label="监控台工具">
      <button type="button" className={`favorite-toggle ${selectedIsFavorite ? 'active' : ''}`} disabled={!driver} aria-pressed={selectedIsFavorite}
        aria-label={`${selectedIsFavorite ? '取消关注' : '关注'} ${driver?.name ?? '所选车手'}`}
        onClick={() => { if (driver) setFavorites(previous => toggleFavorite(previous, driver)); }}>
        <Star size={12} fill={selectedIsFavorite ? 'currentColor' : 'none'} />{driver?.code ?? '车手'}<span className="toolbar-button-label">{selectedIsFavorite ? '已关注' : '关注'}</span>
      </button>
      <div className="dashboard-tool-actions">
        <details className="layout-menu" ref={layoutMenu} onKeyDown={event => { if (event.key === 'Escape') { event.currentTarget.open = false; event.currentTarget.querySelector('summary')?.focus(); } }}>
          <summary><PanelsTopLeft size={12}/><span>布局</span></summary>
          <div className="layout-options" aria-label="布局设置">
            <label><input type="checkbox" checked={showLapHistory} onChange={event => { setShowLapHistory(event.target.checked); if (event.target.checked) setMobileView('field'); }}/><span>圈速对比</span></label>
            <button type="button" onClick={() => { setSavedWidths({ ...DEFAULT_PANEL_WIDTHS }); setShowLapHistory(false); if (layoutMenu.current) layoutMenu.current.open = false; }}><RotateCcw size={12}/><span>恢复默认布局</span></button>
          </div>
        </details>
        <button type="button" onClick={() => void toggleFullscreen()} aria-pressed={fullscreen} title={document.fullscreenEnabled ? '切换全屏监控' : '当前浏览器不支持页面全屏'}>
          {fullscreen ? <Minimize2 size={12} /> : <Maximize2 size={12} />}<span>{fullscreen ? '退出全屏' : '全屏'}</span>
        </button>
      </div>
    </div>
    {notice && <div className="dashboard-notice" role="status"><span>{notice}</span><button type="button" aria-label="关闭提示" onClick={() => setNotice('')}><X size={13} /></button></div>}
    <nav className="mobile-dashboard-nav" aria-label="监控面板切换">{mobileViews.map(item => { const Icon = item.icon; return <button type="button" key={item.id} className={mobileView === item.id ? 'active' : ''} aria-current={mobileView === item.id ? 'page' : undefined} onClick={() => setMobileView(item.id)}><Icon size={15} />{results && item.id === 'timing' ? reviewAtMs == null ? '成绩' : '计时' : results && item.id === 'field' ? '回顾' : item.label}</button>; })}</nav>
    <div ref={grid.ref} className="dashboard-grid" style={{ '--timing-width': `${widths.timingWidth}px`, '--sidebar-width': `${widths.sidebarWidth}px` } as CSSProperties}>
      <TimingTower sessionView={sessionView} result={results && result && reviewAtMs == null ? result : undefined} selectedDriver={driver?.number ?? selectedDriver} onSelectDriver={selectDriver} favoriteDrivers={favoriteDrivers} sectorRecords={records.sectors} animateRanks={animateUpdates}/>
      <div className={`dashboard-center ${showLapHistory ? 'with-lap-history' : ''}`}><RaceStudio sessionView={sessionView} chart={chart} selectedDriver={driver?.number ?? selectedDriver} onSelectDriver={selectDriver} presentation={presentation} followingLive={followingLive} onReturnLive={onReturnLive} playback={playback} onReviewAt={onReviewAt} onInspect={onInspect} processNotice={processNotice}/>{showLapHistory && <DriverLapHistory sessionView={sessionView} driver={driver} compareDriver={compareDriver} onCompareDriverChange={number => { onInspect?.(); setCompareNumber(number); }} onInspectLap={inspectLap} onClose={() => setShowLapHistory(false)}/>}<WeatherStrip sessionView={sessionView} results={results && reviewAtMs == null}/></div>
      <RaceFeed sessionView={sessionView} selectedDriver={driver?.number ?? selectedDriver} onSelectDriver={selectDriver} />
      <PanelResizer side="timing" widths={widths} containerWidth={grid.width} onChange={setSavedWidths} />
      <PanelResizer side="sidebar" widths={widths} containerWidth={grid.width} onChange={setSavedWidths} />
    </div>
  </div>;
}
